import { MessageCircle, NotebookPen } from 'lucide-react';
import { createContext, useContext, useState } from 'react';

import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import { useTabLabels } from '@/hooks/useTabLabels';
import { useCourseEditStore } from '@/stores/courseEditStore';
import LessonPlansPanel from '../CourseLessonPlansTab/LessonPlansPanel';
import CourseComments from './CourseComments';

// Shared open state so the trigger button (rendered in the header) can open
// the drawer even though the Drawer root lives elsewhere in the tree.
const CommentDrawerContext = createContext<{
	open: boolean;
	setOpen: (v: boolean) => void;
	isLessonPlans: boolean;
}>({ open: false, setOpen: () => {}, isLessonPlans: false });

export const CommentDrawerProvider = ({ children }: { children: React.ReactNode }) => {
	const { commentTypes } = useCourseEditStore();
	const [open, setOpen] = useState(false);
	const isLessonPlans = commentTypes.includes('none');
	return (
		<CommentDrawerContext.Provider value={{ open, setOpen, isLessonPlans }}>
			{children}
		</CommentDrawerContext.Provider>
	);
};

/** Floating trigger button — place this wherever it should appear (e.g. CourseHeader on mobile). */
export const CommentDrawerTrigger = () => {
	const { setOpen, isLessonPlans } = useContext(CommentDrawerContext);
	const labels = useTabLabels();
	return (
		<button
			type="button"
			aria-label={isLessonPlans ? 'Open lesson plan panel' : 'Open comments'}
			onClick={() => setOpen(true)}
			className="md:hidden flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-border bg-background hover:bg-secondary text-[12px] text-foreground transition-colors"
		>
			{isLessonPlans ? <NotebookPen size={15} /> : <MessageCircle size={15} />}
			<span>{isLessonPlans ? labels.lessonPlans : 'Comments'}</span>
		</button>
	);
};

/** The Drawer itself — rendered once, near the bottom of the tree. */
const CommentDrawer = () => {
	const { open, setOpen, isLessonPlans } = useContext(CommentDrawerContext);
	const labels = useTabLabels();

	return (
		<Drawer open={open} onOpenChange={setOpen} direction="right" shouldScaleBackground={false}>
			{/* No DrawerTrigger here — trigger is handled by CommentDrawerTrigger above */}
			<DrawerContent
				hideHandle
				className="top-0 right-0 left-auto w-[300px] md:w-[320px] h-full rounded-none border-l border-border shadow-xl"
				style={{ '--initial-transform': 'calc(100% + 8px)' } as React.CSSProperties}
			>
				<DrawerTitle className="sr-only" data-testid="drawer-title">
					{isLessonPlans ? labels.lessonPlans : 'Comments'}
				</DrawerTitle>
				<DrawerDescription className="sr-only">
					{isLessonPlans ? labels.lessonPlans : 'Comments'}
				</DrawerDescription>
				<div className="flex flex-col h-full overflow-hidden">
					{isLessonPlans ? <LessonPlansPanel /> : <CourseComments />}
				</div>
			</DrawerContent>
		</Drawer>
	);
};

export default CommentDrawer;
